import type { PluginCatalogItem } from '@crowdex/plugin-contracts';
import { cn } from '@crowdex/ui/lib/utils';
import {
  AppWindow,
  Blocks,
  Bot,
  type LucideIcon,
  Palette,
  Plug,
  Server,
  Sparkles,
} from 'lucide-react';
import { ItemIcon } from '@/components/item-icon';

type Size = 'small' | 'default' | 'large';

const BOX: Record<Size, string> = {
  small: 'size-8',
  default: 'size-10',
  large: 'size-14',
};
const ICON: Record<Size, string> = { small: 'size-5', default: 'size-6', large: 'size-9' };
const GLYPH: Record<Size, string> = { small: 'size-4', default: 'size-5', large: 'size-7' };

/**
 * A logo the app can paint in the theme's ink: a vector image (an app's template icon, an SVG).
 * A raster image, or an SVG that only spells initials, is no logo (`null`): its kind's glyph
 * stands for it instead, so GitHub and GitLab never both read "GI".
 */
export function inkLogoSource(url: string | null | undefined): string | null {
  if (url === null || url === undefined || url === '') return null;
  if (url.startsWith('data:')) {
    if (!url.startsWith('data:image/svg+xml')) return null;
    const comma = url.indexOf(',');
    const header = url.slice(0, comma);
    const body = url.slice(comma + 1);
    let text = '';
    try {
      text = header.endsWith(';base64') ? atob(body) : decodeURIComponent(body);
    } catch {
      return null;
    }
    return /<text[\s>]/.test(text) ? null : url;
  }
  const path = url.split(/[?#]/)[0] ?? '';
  return path.endsWith('.svg') || /^\/api\/plugins\/[^/]+\/icon$/.test(path) ? url : null;
}

/** A glyph or a logo in ink, in a box of the logo's size: no tile, no colour (option B). */
function InkMark({ size, src, icon }: { size: Size; src: string | null; icon: LucideIcon }) {
  return (
    <span
      aria-hidden
      className={cn(BOX[size], 'flex shrink-0 items-center justify-center text-foreground')}
    >
      {src === null ? (
        <ItemIcon className={GLYPH[size]} icon={icon} />
      ) : (
        <ItemIcon className={ICON[size]} icon={icon} src={src} />
      )}
    </span>
  );
}

/**
 * A service's mark (Connections, "Connect an account"): its brand logo when the app has a vector
 * one, else the connector glyph.
 */
export function ServiceLogo({
  logoUrl,
  size = 'default',
}: {
  logoUrl?: string | null | undefined;
  size?: Size;
}) {
  return <InkMark icon={Plug} size={size} src={inkLogoSource(logoUrl)} />;
}

/** The glyph of what a plugin mainly is: an app, a connector, an agent, skills… */
function pluginKindIcon(plugin: PluginCatalogItem): LucideIcon {
  if ('crowdex' in plugin && plugin.crowdex !== undefined) return AppWindow;
  if (plugin.service !== null || plugin.contributes.includes('connectors')) return Plug;
  if (plugin.contributes.includes('agents')) return Bot;
  if (plugin.contributes.includes('apps')) return AppWindow;
  if (plugin.contributes.includes('mcp')) return Server;
  if (plugin.contributes.includes('skills')) return Sparkles;
  if (plugin.contributes.includes('themes')) return Palette;
  return Blocks;
}

export function PluginLogo({
  plugin,
  size = 'default',
}: {
  plugin: PluginCatalogItem;
  size?: Size;
}) {
  // A Crowdex app's icon is a template image; other vector logos are painted the same way.
  return <InkMark icon={pluginKindIcon(plugin)} size={size} src={inkLogoSource(plugin.logoUrl)} />;
}
